import { AxiosResponse } from "axios";
import { deepCopy } from "@/helpers/utilities";
import { Contact } from "@/models/contact";
import { ContactFilterForm } from "@/models/contact-filter";
import {
  ContactGroup,
  IContactGroupList,
  ContactGroupMemberList,
} from "@/models/contact-group";
import { BehaviorSubject } from "rxjs";
import axios from "@/services/axios";

export interface AddContactGroupMembersState {
  contactFolderId: string;
  contactFilters: ContactFilterForm[];
  searchString: string;
  addAllFilteredContacts: boolean;
  currentStep: number;
  totalSteps: number;
}

export const defaultAddContactGroupMembersState: AddContactGroupMembersState = {
  contactFolderId: "",
  contactFilters: [{ ...new ContactFilterForm(), condition: undefined }],
  searchString: "",
  addAllFilteredContacts: true,
  currentStep: 0,
  totalSteps: 3,
};

class ContactGroupService {
  getContactGroups(
    contactFolderId: string,
    page: number,
    size: number,
    search: string,
    sortOrder: string,
    sortField: string = "contactGroupName",
  ): Promise<AxiosResponse<IContactGroupList>> {
    const dir =
      sortOrder === "descend" ? sortField + ",DESC" : sortField + ",ASC";
    const sort = sortField && sortOrder != "none" ? dir : "";
    return axios.get(`/api/contact-groups/${contactFolderId}`, {
      params: {
        page,
        size,
        search,
        sort,
      },
    });
  }

  updateContactGroup(
    group: ContactGroup,
    contactFolderId: string,
  ): Promise<AxiosResponse<ContactGroup>> {
    return axios.put(`/api/contact-groups/${contactFolderId}`, group);
  }

  createContactGroup(
    group: ContactGroup,
    contactFolderId: string,
  ): Promise<AxiosResponse<ContactGroup>> {
    return axios.post(`/api/contact-groups/${contactFolderId}`, group);
  }

  deleteContactGroup(group: ContactGroup): Promise<AxiosResponse> {
    return axios.delete(`/api/contact-groups/${group.contactGroupId}`);
  }

  getContactGroupMembers(
    contactGroupId: string,
    page: number,
    size: number,
    sort: string = "",
  ): Promise<AxiosResponse<ContactGroupMemberList>> {
    return axios.get(
      `/api/contact-groups/contact-group/${contactGroupId}/members`,
      {
        params: {
          size,
          page,
          sort,
        },
      },
    );
  }

  removeContactFromContactGroup(memberId: string): Promise<any> {
    return axios.delete<any>(`/api/contact-groups/members/${memberId}`);
  }

  removeContactFilter(contactFilterId: string): Promise<AxiosResponse> {
    return axios.delete(`/api/contact-query-filter/${contactFilterId}`);
  }

  addContactsInBulk(
    contactGroupId: string,
    searchString: string,
    requestBody: any,
  ): Promise<AxiosResponse> {
    return axios.post(
      `/api/contact-groups/${contactGroupId}/add-members`,
      requestBody,
      {
        params: {
          search: searchString,
        },
      },
    );
  }

  createContactGroupQueryFilter(data: any): Promise<AxiosResponse> {
    return axios.post(
      `/api/contact-query-filter/${data.contact_folder_id}/add-query-filter`,
      data,
      {
        params: {
          filterQuery: data.filterQuery,
          contactFilterName: data.contactFilterName,
          description: data.description,
        },
      },
    );
  }

  updateContactGroupQueryFilter(data: any): Promise<AxiosResponse> {
    return axios.put(
      `/api/contact-query-filter/${data.contact_folder_id}`,
      data,
    );
  }

  getContactFilters(
    page: number,
    size: number,
    search: string,
    contactFolderId: string,
    sortField?: string,
    sortOrder?: string,
  ): Promise<AxiosResponse> {
    const dir =
      sortOrder === "descend" ? sortField + ",DESC" : sortField + ",ASC";
    const sort = sortField && sortOrder != "none" ? dir : "";
    return axios.get(`/api/contact-query-filter/${contactFolderId}`, {
      params: {
        page,
        size,
        search,
        sort,
        contact_folder_id: contactFolderId,
      },
    });
  }

  getContactFilterById(contactFilterId: string) {
    return axios.get(
      `/api/contact-query-filter/${contactFilterId}/contact-filter`,
    );
  }

  getContactGroupById(groupId: string) {
    return axios.get(`/api/contact-groups/contact-group/${groupId}`);
  }

  deleteAllMembersFromContactGroup(
    contactGroupId: string | undefined,
    username: string,
  ) {
    return axios.delete(
      `/api/contact-groups/${contactGroupId}/members?key=${username}`,
    );
  }

  private readonly addGroupMembersState =
    new BehaviorSubject<AddContactGroupMembersState>(
      defaultAddContactGroupMembersState,
    );
  readonly addGroupMembersState$ = this.addGroupMembersState.asObservable();

  public resetAddGroupMembersState() {
    this.addGroupMembersState.next({ ...defaultAddContactGroupMembersState });
  }

  public nextStep() {
    this.addGroupMembersState.next(
      deepCopy({
        ...this.addGroupMembersState.value,
        currentStep: this.addGroupMembersState.value.currentStep + 1,
      }),
    );
  }

  public previousStep() {
    this.addGroupMembersState.next(
      deepCopy({
        ...this.addGroupMembersState.value,
        currentStep: this.addGroupMembersState.value.currentStep - 1,
      }),
    );
  }

  public updateContactFilters(
    contactFolderId: string,
    contactFilters: ContactFilterForm[],
    searchString: string,
  ) {
    this.addGroupMembersState.next(
      deepCopy({
        ...this.addGroupMembersState.value,
        contactFolderId,
        contactFilters: [...contactFilters],
        searchString,
      }),
    );
  }

  public submitContactSelection(
    addAllFilteredContacts: boolean,
    selectedContacts: Contact[],
    unselectedContacts: Contact[],
    additionalSelectedContacts: Contact[],
    searchSpecification: any,
    contactFolderId: string,
  ) {
    this.addGroupMembersState.next(
      deepCopy({ ...this.addGroupMembersState.value, addAllFilteredContacts }),
    );

    const requestBody = {
      contactFolderId: contactFolderId,
      allContactsSelected: addAllFilteredContacts,
      unselectedContacts,
      selectedContacts,
      additionalSelectedContacts,
      searchSpecification: searchSpecification || undefined,
    };
    return requestBody;
  }
}
export const contactGroupService = new ContactGroupService();
